// =============================================================================
// QuestionnaireSettings — Template library sourced from Clinio Forms
// =============================================================================
// Linear: CLINIO-217 (FORMS-6)
//
// Replaces the legacy v2 questionnaire_templates CRUD page with a read-only
// view of the Forms-hosted template library. Clinics can:
//   - Browse platform PROMs (read-only, "Platform-skabelon" badge)
//   - Browse their own custom templates
//   - Click "Rediger i Clinio Forms" to edit custom templates in Forms admin
//   - Preview a template in a modal before using it
//
// Template editing lives entirely in Clinio Forms admin UI.
// This page does NOT host a questionnaire editor (v2 spec, FORMS-1 ADD §12).
// =============================================================================

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  Eye,
  Filter,
  Loader2,
  Link2,
  X,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { formsQuestionnaireEditPath } from '@/lib/formsUrls';
import { isUsableFormsGrant } from '@/lib/formsStaffAccess';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import {
  useFormsTemplateList,
  useFormsTemplateDetail,
  type FormsTemplateSummary,
} from '@/hooks/useFormsTemplates';
import {
  useFormsTenantStatus,
  useProvisionFormsTenant,
} from '@/hooks/useClinioForms';
import { useFormsStaffAccess } from '@/hooks/useFormsStaffAccess';
import { IntegrationStatusBanner } from '@/components/IntegrationStatusBanner';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';
import {
  FormsLinkingHelp,
  FormsSetupGuide,
  type FormsSetupStatus,
} from '@/pages/settings/FormsSetupGuide';
import { FormsSsoButton } from '@/pages/settings/FormsSsoButton';
import { FormsStaffAccessSection } from '@/pages/settings/FormsStaffAccessSection';
import { QuestionnaireTagChips, QuestionnaireTagFilter } from '@/components/questionnaires/QuestionnaireTagFilter';
import { collectQuestionnaireTags, matchesQuestionnaireTags } from '@/lib/questionnaireTags';


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TemplateBadge({ variant, label }: { variant: 'platform' | 'license' | 'custom'; label: string }) {
  const styles = {
    platform: 'bg-ds-teal-tint text-ds-teal',
    license:  'bg-ds-amber-tint text-ds-amber-ink',
    custom:   'bg-ds-neutral-tint text-ds-muted',
  } as const;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[variant]}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ConnectBanner — shown when clinic is not yet provisioned in Clinio Forms
// ---------------------------------------------------------------------------

function ConnectBanner({
  clinicId,
  onConnected,
}: {
  clinicId: string;
  onConnected: () => void;
}) {
  const { t } = useTranslation();
  const provision = useProvisionFormsTenant(clinicId);

  const handleConnect = async () => {
    try {
      await provision.mutateAsync();
      onConnected();
    } catch (_e) {
      // error surfaced via provision.error below
    }
  };

  return (
    <div className="rounded-xl border border-ds-teal-line bg-ds-teal-tint p-6">
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-ds-teal-tint p-2.5 ">
          <Link2 className="h-5 w-5 text-ds-teal" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-ds-teal">
            {t('questionnaires.connect_title')}
          </h3>
          <p className="mt-1 text-sm text-ds-teal">
            {t('questionnaires.connect_desc')}
          </p>
          {provision.error && (
            <div className="mt-s3 rounded-ctl border border-ds-red-line bg-ds-card p-s3">
              <p className={cn(TYPE.small, 'font-medium text-ds-red-ink')}>
                {(provision.error as Error).message}
              </p>
              <FormsLinkingHelp
                className="mt-s3"
                errorMessage={(provision.error as Error).message}
              />
            </div>
          )}
        </div>
        <Button
          onClick={handleConnect}
          disabled={provision.isPending}
          className="shrink-0"
        >
          {provision.isPending ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('questionnaires.connect_loading')}
            </span>
          ) : (
            t('questionnaires.connect_btn')
          )}
        </Button>
      </div>
    </div>
  );
}

type PreviewModalProps = {
  clinicId: string;
  template: FormsTemplateSummary;
  locale: 'da-DK' | 'en-DK';
  onClose: () => void;
};

function PreviewModal({ clinicId, template, locale, onClose }: PreviewModalProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useFormsTemplateDetail(clinicId, template.id);
  const displayName = locale === 'da-DK' ? template.display_name_da : template.display_name_en;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-16 max-w-lg rounded-xl bg-ds-card shadow-pop">
        <div className="flex items-center justify-between border-b border-ds-line px-6 py-4">
          <h3 className="text-base font-semibold text-ds-text">{displayName}</h3>
          <Button type="button" variant="tertiary" size="icon" onClick={onClose} aria-label={t('common.close')}>
            <X />
          </Button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
            </div>
          ) : data ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {template.is_platform && (
                  <TemplateBadge
                    variant="platform"
                    label={locale === 'da-DK'
                      ? t('questionnaires.platform_template_badge')
                      : t('questionnaires.platform_template_badge')}
                  />
                )}
                {template.seed_status === 'content_pending_license' && (
                  <TemplateBadge
                    variant="license"
                    label={locale === 'da-DK'
                      ? t('questionnaires.license_pending_badge')
                      : t('questionnaires.license_pending_badge_en')}
                  />
                )}
              </div>

              {data.description && (
                <p className="text-sm text-ds-muted">{data.description}</p>
              )}
              <QuestionnaireTagChips tags={data.tags?.length ? data.tags : template.tags ?? []} />

              <dl className="grid grid-cols-2 gap-3 text-sm">
                {data.estimated_minutes != null && (
                  <>
                    <dt className="text-ds-muted">{t('questionnaires.estimated_minutes')}</dt>
                    <dd className="font-medium text-ds-text">
                      {data.estimated_minutes} min
                    </dd>
                  </>
                )}
                {data.category && (
                  <>
                    <dt className="text-ds-muted">{t('questionnaires.category')}</dt>
                    <dd className="font-medium text-ds-text capitalize">
                      {data.category.replace(/_/g, ' ')}
                    </dd>
                  </>
                )}
                <dt className="text-ds-muted">{t('questionnaires.version')}</dt>
                <dd className="font-medium text-ds-text">{data.version}</dd>
              </dl>

              {data.seed_status === 'content_pending_license' && (
                <div className="rounded-lg bg-ds-amber-tint p-3 text-sm text-ds-amber-ink">
                  {t('questionnaires.license_pending_notice')}
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-sm text-ds-muted">
              {t('questionnaires.preview_unavailable')}
            </p>
          )}
        </div>

        <div className="flex justify-end border-t border-ds-line px-6 py-4">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

type TemplateCardProps = {
  template: FormsTemplateSummary;
  locale: 'da-DK' | 'en-DK';
  clinicId: string;
  hasFormsGrant: boolean;
  onPreview: (t: FormsTemplateSummary) => void;
};

function TemplateCard({ template, locale, clinicId, hasFormsGrant, onPreview }: TemplateCardProps) {
  const { t } = useTranslation();
  const displayName = locale === 'da-DK' ? template.display_name_da : template.display_name_en;
  const isPendingLicense = template.seed_status === 'content_pending_license';

  return (
    <div className="flex items-start gap-4 rounded-xl border border-ds-line bg-ds-card p-4 shadow-card">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-ds-text">{displayName}</p>

          {template.is_platform && (
            <TemplateBadge
              variant="platform"
              label={locale === 'da-DK'
                ? t('questionnaires.platform_template_badge')
                : t('questionnaires.platform_template_badge_en')}
            />
          )}

          {isPendingLicense && (
            <TemplateBadge
              variant="license"
              label={locale === 'da-DK'
                ? t('questionnaires.license_pending_badge')
                : t('questionnaires.license_pending_badge_en')}
            />
          )}
        </div>

        {template.description && (
          <p className="mt-1 text-sm text-ds-muted">{template.description}</p>
        )}
        <QuestionnaireTagChips tags={template.tags ?? []} />

        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ds-muted">
          {template.estimated_minutes != null && (
            <span className="flex items-center gap-1">
              <ClipboardList className="h-3.5 w-3.5" />
              {template.estimated_minutes} min
            </span>
          )}
          {template.category && (
            <span className="capitalize">{template.category.replace(/_/g, ' ')}</span>
          )}
          <span className="font-mono">{template.version}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="tertiary"
          size="icon"
          title={t('questionnaires.preview')}
          onClick={() => onPreview(template)}
        >
          <Eye />
        </Button>

        {!template.is_platform && clinicId && (
          <FormsSsoButton
            clinicId={clinicId}
            hasGrant={hasFormsGrant}
            redirectPath={formsQuestionnaireEditPath(template.id)}
            label={t('questionnaires.edit_in_forms')}
            variant="secondary"
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QuestionnaireSettings() {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { user } = useAuth();

  const uiLocale: 'da-DK' | 'en-DK' = i18n.language?.startsWith('da') ? 'da-DK' : 'en-DK';

  const [filterLocale, setFilterLocale] = useState<'' | 'da-DK' | 'en-DK'>('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [previewTemplate, setPreviewTemplate] = useState<FormsTemplateSummary | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const tenantStatus = useFormsTenantStatus(activeClinicId);
  const formsStatus: FormsSetupStatus = tenantStatus.data?.status ?? 'not_provisioned';
  const isProvisioned = formsStatus === 'active';
  const staffAccess = useFormsStaffAccess(isProvisioned ? activeClinicId : null);
  const hasOwnGrant = Boolean(
    user?.id &&
      staffAccess.data?.some((row) => row.user_id === user.id && isUsableFormsGrant(row)),
  );
  const tenantStatusError =
    tenantStatus.error instanceof Error ? tenantStatus.error.message : undefined;

  const templatesQuery = useFormsTemplateList(
    activeClinicId,
    filterLocale || undefined,
  );

  const library = useMemo(() => {
    if (templatesQuery.data) return templatesQuery.data;
    return null;
  }, [templatesQuery.data]);

  const allCategories = useMemo(() => {
    if (!library) return [];
    const cats = new Set<string>();
    [...library.platform, ...library.clinic].forEach((tpl) => {
      if (tpl.category) cats.add(tpl.category);
    });
    return Array.from(cats).sort();
  }, [library]);

  const allTags = useMemo(() => {
    if (!library) return [];
    return collectQuestionnaireTags([...library.platform, ...library.clinic]);
  }, [library]);

  function applyFilter(items: FormsTemplateSummary[]) {
    return items.filter((tpl) => {
      if (filterCategory && tpl.category !== filterCategory) return false;
      return matchesQuestionnaireTags(tpl.tags, selectedTags);
    });
  }

  const platformTemplates = library ? applyFilter(library.platform) : [];
  const clinicTemplates   = library ? applyFilter(library.clinic)   : [];

  const isEmpty = !templatesQuery.isLoading && platformTemplates.length === 0 && clinicTemplates.length === 0;

  return (
    <div>
      <IntegrationsBackLink />

      {/* Clinio Forms service availability banner (SHARED-5) */}
      <IntegrationStatusBanner integration="forms" className="mb-4" />

      {tenantStatus.isError && (
        <div className="mb-s4 rounded-card border border-ds-red-line bg-ds-red-tint p-s4">
          <p className={cn(TYPE.body, 'text-ds-red-ink')}>
            {t('questionnaires.setup_guide.status_load_error')}
          </p>
          <FormsLinkingHelp className="mt-s3" errorMessage={tenantStatusError} />
        </div>
      )}

      {/* ── Setup guide (prominent while Forms is not connected) ── */}
      {!tenantStatus.isLoading && !isProvisioned && (
        <FormsSetupGuide
          className="mb-s4"
          connected={false}
          status={formsStatus}
        />
      )}

      {/* ── Connect banner (shown when clinic not yet provisioned) ── */}
      {!tenantStatus.isLoading && !isProvisioned && activeClinicId && (
        <div className="mb-s4">
          <ConnectBanner
            clinicId={activeClinicId}
            onConnected={() => tenantStatus.refetch()}
          />
        </div>
      )}

      {/* ── Header ── */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ds-text">
            {t('settings.integrations.cards.forms_title')}
          </h2>
          <p className="mt-1 text-sm text-ds-muted">
            {t('questionnaires.forms_subtitle')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isProvisioned && activeClinicId && (
            <FormsSsoButton
              clinicId={activeClinicId}
              hasGrant={hasOwnGrant}
              label={t('questionnaires.open_forms_admin')}
            />
          )}
          <Button type="button" variant="secondary" onClick={() => setShowFilters((v) => !v)}>
            <Filter />
            {t('questionnaires.filter')}
          </Button>
        </div>
      </div>

      {/* ── Setup guide (collapsible once Forms is connected) ── */}
      {!tenantStatus.isLoading && isProvisioned && (
        <FormsSetupGuide
          className="mb-s4"
          connected
          status={formsStatus}
          openFormsAction={
            activeClinicId ? (
              <FormsSsoButton
                clinicId={activeClinicId}
                hasGrant={hasOwnGrant}
                label={t('questionnaires.setup_guide.admin_link')}
                variant="secondary"
              />
            ) : null
          }
        />
      )}

      {isProvisioned && activeClinicId && (
        <FormsStaffAccessSection clinicId={activeClinicId} />
      )}

      {allTags.length > 0 ? (
        <div className="mb-s4">
          <QuestionnaireTagFilter tags={allTags} selected={selectedTags} onChange={setSelectedTags} />
        </div>
      ) : null}

      {/* ── Filter panel ── */}
      {showFilters && (
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-ds-line bg-ds-sunk p-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-ds-text">
              {t('questionnaires.filter_locale')}
            </label>
            <select
              value={filterLocale}
              onChange={(e) => setFilterLocale(e.target.value as '' | 'da-DK' | 'en-DK')}
              className="rounded-lg border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text shadow-card focus:border-ds-teal focus:outline-none focus:shadow-ring"
            >
              <option value="">{t('questionnaires.filter_all')}</option>
              <option value="da-DK">{t('forms.picker.language_da', 'Dansk')}</option>
              <option value="en-DK">{t('forms.picker.language_en', 'Engelsk')}</option>
            </select>
          </div>

          {allCategories.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-ds-text">
                {t('questionnaires.category')}
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="rounded-lg border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text shadow-card focus:border-ds-teal focus:outline-none focus:shadow-ring"
              >
                <option value="">{t('questionnaires.filter_all')}</option>
                {allCategories.map((cat) => (
                  <option key={cat} value={cat} className="capitalize">
                    {cat.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(filterLocale || filterCategory || selectedTags.length > 0) && (
            <Button
              type="button"
              variant="link"
              onClick={() => {
                setFilterLocale('');
                setFilterCategory('');
                setSelectedTags([]);
              }}
            >
              <X />
              {t('questionnaires.clear_filters')}
            </Button>
          )}
        </div>
      )}

      {/* ── Loading ── */}
      {templatesQuery.isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      )}

      {/* ── Error ── */}
      {templatesQuery.isError && !templatesQuery.isLoading && (
        <div className="rounded-xl border border-ds-red-line bg-ds-red-tint p-4 text-sm text-ds-red-ink">
          <p>{t('questionnaires.load_error')}</p>
          <FormsLinkingHelp
            className="mt-s3"
            errorMessage={
              templatesQuery.error instanceof Error ? templatesQuery.error.message : undefined
            }
          />
        </div>
      )}

      {/* ── Empty ── */}
      {isEmpty && !templatesQuery.isLoading && !templatesQuery.isError && (
        <div className="rounded-xl border border-dashed border-ds-line p-10 text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-ds-muted" />
          <p className="mt-4 text-sm text-ds-muted">
            {isProvisioned
              ? t('questionnaires.no_templates_connected')
              : t('questionnaires.no_templates_forms')}
          </p>
          <FormsLinkingHelp className="mx-auto mt-s4 max-w-lg text-left" />
          {isProvisioned && activeClinicId && (
            <FormsSsoButton
              className="mt-4"
              clinicId={activeClinicId}
              hasGrant={hasOwnGrant}
              label={t('questionnaires.create_in_forms')}
            />
          )}
        </div>
      )}

      {/* ── Platform PROM library ── */}
      {!templatesQuery.isLoading && platformTemplates.length > 0 && (
        <section className="mb-8">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ds-muted">
            {t('questionnaires.platform_section')}
          </h3>
          <div className="space-y-3">
            {platformTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                locale={uiLocale}
                clinicId={activeClinicId ?? ''}
                hasFormsGrant={hasOwnGrant}
                onPreview={setPreviewTemplate}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Clinic custom templates ── */}
      {!templatesQuery.isLoading && clinicTemplates.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ds-muted">
            {t('questionnaires.custom_section')}
          </h3>
          <div className="space-y-3">
            {clinicTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                locale={uiLocale}
                clinicId={activeClinicId ?? ''}
                hasFormsGrant={hasOwnGrant}
                onPreview={setPreviewTemplate}
              />
            ))}
          </div>
        </section>
      )}


      {/* ── Preview modal ── */}
      {previewTemplate && activeClinicId && (
        <PreviewModal
          clinicId={activeClinicId}
          template={previewTemplate}
          locale={uiLocale}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
}
